import React, { useCallback } from 'react';
import { batch, useDispatch } from 'react-redux';
import { Form, message } from 'antd';
import { API, graphqlOperation } from 'aws-amplify';
import ReactGA from 'react-ga';

import {
  Giide,
  // IState,
  // SocialUrls
} from '../../graphql/types';

import useGiideSettings from '../../hooks/useGiideSettings';
// import useMutation from '../../hooks/useMutation';
import { PublishGiideMutation, PublishState } from '../../graphql/api';
import {
  updateGiide,
  setPublishActiveStep,
  clearEditorData,
  setGiideTabState,
} from '../../actions';
import { GiidePublishStateObj } from '../../types';

import PublishFlow from './PublishFlow';
import { publishGiide } from '../../graphql/mutations';

interface IProps {
  giide: Giide;
  tenantId: string;
  userId: string;
  publishing: GiidePublishStateObj;
  setPublishing: React.Dispatch<React.SetStateAction<GiidePublishStateObj>>;
  closeModal: () => void;
  open: boolean;
}

const Publish: React.FC<IProps> = ({
  giide,
  tenantId,
  userId,
  publishing,
  setPublishing,
  closeModal,
  open,
}) => {
  const {
    giideData,
    updateInput,
    updateColor,
    updateShade,
    updateThumbnail,
    updateSpeakerInput,
    updateSpeakerUrlInput,
    updateSpeakerName,
    avatar,
    handleAvatarUpload,
    removeAvatarHandler,
    uploadAvatarProgress,
    uploadThumbnail,
    removeThumbnail,
    selectCustomThumbnail,
    updateThumbnailData,
    setGiideData,
    updateGiideTitleName,
  } = useGiideSettings(giide, tenantId, userId);

  const [detailsForm] = Form.useForm();
  const [imageForm] = Form.useForm();

  const dispatch = useDispatch();

  const saveAndPublish = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      try {
        await detailsForm.validateFields();
        await imageForm.validateFields();
      } catch (e) {
        message.error('Please fill in all required fields.');
        return;
      }

      try {
        setPublishing({ [giideData.id]: true });
        const result = await (API.graphql(
          graphqlOperation(publishGiide, {
            input: {
              giideId: giideData.id,
              userId,
              categoryIndex: 0,
            },
          })
        ) as Promise<{ data: PublishGiideMutation }>);

        if (result.data.publishGiide?.result === 'ok') {
          ReactGA.event({
            category: 'Button',
            action: 'Publish Button Success',
          });
          batch(() => {
            dispatch(
              updateGiide(
                { publishState: PublishState.published } as Giide,
                true
              )
            );
            dispatch(clearEditorData(true));
            setPublishing({ [giideData.id]: false });
            dispatch(setGiideTabState('published'));
            dispatch(setPublishActiveStep(0));

            // fixes modal not closing when publishing from header dropdown menu
            if (closeModal) closeModal();
          });
        } else {
          throw new Error('Error occurred during publishing.');
        }
      } catch (e) {
        setPublishing({ [giideData.id]: false });
        message.error('Error occurred during publishing.');
      }
    },
    [
      giideData,
      dispatch,
      detailsForm,
      imageForm,
      userId,
      setPublishing,
      closeModal,
    ]
  );

  // const closePublishSuccessModal = useCallback(async () => {
  //   const giideDataInput = {
  //     ...giideData,
  //     noindex: giideData.noindex,
  //   };
  //   const updatedGiide = await callMutation(giideDataInput);
  //   if (updatedGiide) {
  //     dispatch(setPublishActiveStep(0));
  //     dispatch(updateGiideSettings(giideDataInput));
  //     dispatch(clearEditorData(true));
  //   }
  //   closeModal();
  // }, [callMutation, closeModal, dispatch, giideData]);

  const handleCancelPublishing = () => {
    batch(() => {
      dispatch(setPublishActiveStep(0));
      dispatch(clearEditorData(true));
      closeModal();
    });
  };

  return (
    <PublishFlow
      open={open}
      giideData={giideData}
      publishing={publishing}
      closeModal={handleCancelPublishing}
      updateInput={updateInput}
      updateColor={updateColor}
      updateShade={updateShade}
      updateThumbnail={updateThumbnail}
      detailsForm={detailsForm}
      uploadThumbnail={uploadThumbnail}
      removeThumbnail={removeThumbnail}
      selectCustomThumbnail={selectCustomThumbnail}
      updateSpeakerInput={updateSpeakerInput}
      updateSpeakerUrlInput={updateSpeakerUrlInput}
      updateSpeakerName={updateSpeakerName}
      updateThumbnailData={updateThumbnailData}
      imageForm={imageForm}
      uploadAvatarProgress={uploadAvatarProgress}
      avatar={avatar}
      handleAvatarUpload={handleAvatarUpload}
      removeAvatar={removeAvatarHandler}
      saveAndPublish={saveAndPublish}
      setGiideData={setGiideData}
      updateGiideTitleName={updateGiideTitleName}
    />
  );
};

export default Publish;
